import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import './OrderTracking.css';
import { ordersService, BookingOrder } from '../../services/ordersService';

interface TrackingStep {
  id: string;
  title: string;
  description: string;
  status: 'completed' | 'current' | 'pending';
  timestamp?: string;
  icon: string;
}

const OrderTracking: React.FC = () => {
  const { serviceRequestId } = useParams<{ serviceRequestId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [order, setOrder] = useState<BookingOrder | null>(location.state?.order || null);
  const [loading, setLoading] = useState(!order);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    if (!order && serviceRequestId) {
      loadOrderDetails();
    }
  }, [serviceRequestId, order]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      if (order && ['pending', 'confirmed'].includes(order.status)) {
        refreshOrderStatus();
      }
    }, 30000);

    return () => clearInterval(interval);
  }, [order]);

  const loadOrderDetails = async () => {
    if (!serviceRequestId) return;
    
    setLoading(true);
    try {
      const orderDetails = await ordersService.getOrderById(serviceRequestId);
      if (orderDetails) {
        setOrder(orderDetails);
      } else {
        setError('Order not found');
      }
    } catch (err) {
      setError('Failed to load order details');
      console.error('Error loading order:', err);
    } finally {
      setLoading(false);
    }
  };

  const refreshOrderStatus = async () => {
    if (!serviceRequestId) return;
    
    setRefreshing(true);
    try {
      const orderDetails = await ordersService.getOrderById(serviceRequestId);
      if (orderDetails) {
        setOrder(orderDetails);
      }
    } catch (err) {
      console.error('Error refreshing order:', err);
    } finally {
      setRefreshing(false);
    }
  };

  const getTrackingSteps = (order: BookingOrder): TrackingStep[] => {
    const baseSteps: TrackingStep[] = [
      {
        id: 'booking',
        title: 'Booking Confirmed',
        description: 'Your service request has been received and confirmed',
        status: 'completed',
        timestamp: order.createdAt,
        icon: '✅'
      },
      {
        id: 'payment',
        title: 'Payment Processing',
        description: order.paymentMode === 'PREPAID' ? 'Payment completed successfully' : 'Payment will be collected after service',
        status: order.paymentStatus === 'paid' || order.paymentStatus === 'not_required' ? 'completed' : 
               order.paymentStatus === 'pending' ? 'current' : 'pending',
        timestamp: order.paymentStatus === 'paid' ? order.updatedAt : undefined,
        icon: order.paymentMode === 'PREPAID' ? '💳' : '💰'
      },
      {
        id: 'assignment',
        title: 'Priest Assignment',
        description: order.priestDetails ? 
          `${order.priestDetails.name} has been assigned to your service` : 
          'Finding the best priest for your service',
        status: order.priestDetails ? 'completed' : 
               ['pending'].includes(order.status) ? 'current' : 'pending',
        timestamp: order.priestDetails ? order.updatedAt : undefined,
        icon: '👨‍🦲'
      },
      {
        id: 'preparation',
        title: 'Service Preparation',
        description: 'Priest is preparing for your service with required materials',
        status: ['confirmed'].includes(order.status) && order.priestDetails ? 'current' :
               order.priestDetails ? 'completed' : 'pending',
        icon: '🛍️'
      },
      {
        id: 'service',
        title: 'Service in Progress',
        description: `${order.serviceName} ceremony is being performed`,
        status: order.status === 'completed' ? 'completed' : 'pending',
        icon: '🙏'
      },
      {
        id: 'completion',
        title: 'Service Completed',
        description: 'Your spiritual service has been completed successfully',
        status: order.status === 'completed' ? 'completed' : 'pending',
        timestamp: order.status === 'completed' ? order.updatedAt : undefined,
        icon: '🎉'
      }
    ];

    return baseSteps;
  };

  const formatTimestamp = (timestamp: string): string => {
    try {
      return new Date(timestamp).toLocaleString('en-IN', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return timestamp;
    }
  };

  const handleContactPriest = () => {
    if (order?.priestDetails?.phone) {
      window.open(`tel:${order.priestDetails.phone}`);
    }
  };

  const handleWhatsAppPriest = () => {
    if (order?.priestDetails?.phone) {
      const message = `Hello ${order.priestDetails.name}, I am contacting regarding my booking ${order.orderId} scheduled for ${order.serviceDate}`;
      window.open(`https://wa.me/${order.priestDetails.phone}?text=${encodeURIComponent(message)}`);
    }
  };

  if (loading) {
    return (
      <div className="order-tracking-page">
        <div className="tracking-container">
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p>Loading order tracking...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="order-tracking-page">
        <div className="tracking-container">
          <div className="error-state">
            <div className="error-icon">❌</div>
            <h3>Order Not Found</h3>
            <p>{error || 'The requested order could not be found.'}</p>
            <button 
              className="back-button"
              onClick={() => navigate('/orders')}
            >
              Back to Orders
            </button>
          </div>
        </div>
      </div>
    );
  }

  const trackingSteps = getTrackingSteps(order);
  const currentStep = trackingSteps.find(step => step.status === 'current');

  return (
    <div className="order-tracking-page">
      <div className="tracking-container">
        {/* Header */}
        <div className="tracking-header">
          <div className="header-row-1">
            <button 
              className="back-button"
              onClick={() => navigate('/orders')}
            >
              ← Back to Orders
            </button>
            <div className="page-title">
              <h1>Track Your Order</h1>
            </div>
          </div>
          
          <div className="header-row-2">
            <div className="order-details">
              <span className="order-id">{order.orderId}</span>
              <span className="service-name">{order.serviceName}</span>
            </div>
            <button 
              className={`refresh-button ${refreshing ? 'refreshing' : ''}`}
              onClick={refreshOrderStatus}
              disabled={refreshing}
            >
              {refreshing ? '🔄' : '↻'} Refresh
            </button>
          </div>
        </div>

        {/* Current Status Card */}
        <div className="current-status-card">
          <div className="status-content">
            <div className="status-icon">
              {currentStep?.icon || '📋'}
            </div>
            <div className="status-text">
              <h3>{currentStep?.title || 'Order Status'}</h3>
              <p>{currentStep?.description || 'Your order is being processed'}</p>
            </div>
          </div>
          <div className={`status-badge ${order.status}`}>
            {order.status.toUpperCase()}
          </div>
        </div>

        {/* Service Information */}
        <div className="service-info-card">
          <h3>Service Details</h3>
          <div className="service-details-grid">
            <div className="detail-item">
              <span className="icon">📅</span>
              <div>
                <span className="label">Service Date</span>
                <span className="value">{order.serviceDate}</span>
              </div>
            </div>
            <div className="detail-item">
              <span className="icon">🕐</span>
              <div>
                <span className="label">Service Time</span>
                <span className="value">{order.serviceTime}</span>
              </div>
            </div>
            {order.duration && (
              <div className="detail-item">
                <span className="icon">⏱️</span>
                <div>
                  <span className="label">Duration</span>
                  <span className="value">{order.duration}</span>
                </div>
              </div>
            )}
            {/* <div className="detail-item">
              <span className="icon">💰</span>
              <div>
                <span className="label">Amount</span>
                <span className="value">₹{order.totalAmount}</span>
              </div>
            </div> */}
          </div>
        </div>

        {/* Priest Details */}
        {order.priestDetails && (
          <div className="priest-card">
            <h3>Your Assigned Acharya</h3>
            <div className="priest-info">
              <div className="priest-details">
                <div className="priest-name-rating">
                  <h4>{order.priestDetails.name}</h4>
                  <div className="rating">
                    <span className="stars">⭐</span>
                    <span>{order.priestDetails.rating}</span>
                  </div>
                </div>
                <p className="experience">🎓 {order.priestDetails.experience}</p>
                {/* <p className="phone">📞 {order.priestDetails.phone}</p> */}
              </div>
              {/* <div className="priest-actions">
                <button 
                  className="contact-button call"
                  onClick={handleContactPriest}
                >
                  📞 Call
                </button>
                <button 
                  className="contact-button whatsapp"
                  onClick={handleWhatsAppPriest}
                >
                  💬 WhatsApp
                </button>
              </div> */}
            </div>
          </div>
        )}

        {/* Tracking Timeline */}
        <div className="tracking-timeline">
          <h3>Order Progress</h3>
          <div className="timeline-container">
            {trackingSteps.map((step, index) => (
              <div 
                key={step.id} 
                className={`timeline-item ${step.status}`}
              >
                <div className="timeline-marker">
                  <span className="step-icon">{step.icon}</span>
                  {index < trackingSteps.length - 1 && (
                    <div className={`timeline-line ${step.status === 'completed' ? 'completed' : ''}`} />
                  )}
                </div>
                <div className="timeline-content">
                  <h4>{step.title}</h4>
                  <p>{step.description}</p>
                  {step.timestamp && (
                    <span className="timestamp">{formatTimestamp(step.timestamp)}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Address Information */}
        <div className="address-card">
          <h3>Service Address</h3>
          <div className="address-details">
            <strong>{order.address.name || order.address.label}</strong>
            <br />
            {order.address.line1}
            {order.address.line2 && <>, {order.address.line2}</>}
            <br />
            {order.address.city}, {order.address.state} - {order.address.postalCode}
          </div>
        </div>

        {/* Support Section */}
        <div className="support-section">
          <h3>Need Help?</h3>
          <div className="support-actions">
            <button className="support-button">
              📞 Customer Support
            </button>
            <button className="support-button">
              💬 Chat Support
            </button>
            <button className="support-button">
              📧 Email Support
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderTracking;